import { FC, type ReactNode } from 'react';

import { Badge, theme, Tooltip, Typography } from '@/shared/design-system';

import { formatMemberCount, systemRoleDescription } from '../role-descriptions';
import { PanelHeader, PanelHeaderActions, PanelHeaderTitle, PanelTitleRow } from '../styled';
import type { RoleSummary } from '../types';

export const BUILT_IN_BADGE = 'Built-in';
export const CUSTOM_BADGE = 'Custom';

export type RolePanelHeaderProps = {
  role: RoleSummary;
  /** Permissions the role holds, once its detail has loaded. */
  permissionCount?: number;
  /** Everything a role on this plane could hold. */
  paletteSize?: number;
  lockTooltip?: string;
  actions?: ReactNode;
};

export const formatRoleSummaryLine = (
  memberCount: number,
  permissionCount?: number,
  paletteSize?: number,
) =>
  [
    formatMemberCount(memberCount),
    permissionCount === undefined
      ? null
      : paletteSize
        ? `${permissionCount} of ${paletteSize} permissions`
        : `${permissionCount} permissions`,
  ]
    .filter(Boolean)
    .join(' · ');

/** Who the role is for and how big it is, before any tab is opened. */
export const RolePanelHeader: FC<RolePanelHeaderProps> = ({
  role,
  permissionCount,
  paletteSize,
  lockTooltip,
  actions,
}) => {
  const description = systemRoleDescription(role);
  const badge = (
    <Badge
      size="sm"
      variant="subtle"
      color="secondary"
      label={role.isLocked ? BUILT_IN_BADGE : CUSTOM_BADGE}
    />
  );

  return (
    <PanelHeader>
      <PanelHeaderTitle>
        <PanelTitleRow>
          <Typography variant="h6" color={theme.colors.text.primary}>
            {role.name}
          </Typography>
          {role.isLocked && lockTooltip ? (
            <Tooltip title={lockTooltip}>
              <span>{badge}</span>
            </Tooltip>
          ) : (
            badge
          )}
        </PanelTitleRow>
        {description && (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {description}
          </Typography>
        )}
        <Typography variant="body4" color={theme.colors.text.tertiary}>
          {formatRoleSummaryLine(role.memberCount, permissionCount, paletteSize)}
        </Typography>
      </PanelHeaderTitle>

      {actions && <PanelHeaderActions>{actions}</PanelHeaderActions>}
    </PanelHeader>
  );
};
